import { Inject, Injectable } from '@angular/core';
import { SessionExpiredModalService } from '@domgen/dgx-fe-authentication';
import {
  CMS_CONFIG,
  CmsConfig,
  CmsFacade,
  formatCmsContent,
  formatCmsSeoData,
} from '@domgen/dgx-fe-cms';
import { determineIsTablet, filterNullUndefined } from '@domgen/dgx-fe-common';
import { CONFIG, FaviconService, ThemeService } from '@domgen/dgx-fe-config';
import { SeoData, SeoService } from '@domgen/dgx-fe-seo';
import { ComponentStore } from '@ngrx/component-store';
import { CmsRegistrationRoot } from '@us-registration/util-models';
import { Observable, combineLatest } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { AppConfig } from '../../models/config/app-config.type';

export interface ViewModel {
  cmsContent: CmsRegistrationRoot;
  isTablet: boolean;
}

export interface ComponentState {
  cmsContent?: CmsRegistrationRoot;
}

@Injectable()
export class AppComponentService extends ComponentStore<ComponentState> {
  private readonly cmsContent$: Observable<CmsRegistrationRoot> = this.select(
    (state: ComponentState) => state.cmsContent
  ).pipe(filterNullUndefined());

  private readonly updateCmsContent = this.updater(
    (state: ComponentState, cmsContent: CmsRegistrationRoot) => ({
      ...state,
      cmsContent,
    })
  )(this.getRootCmsContent());

  private readonly setSeoData = this.effect(
    (cmsContent$: Observable<CmsRegistrationRoot>) =>
      cmsContent$.pipe(
        map((cmsSeoSite: CmsRegistrationRoot) => formatCmsSeoData(cmsSeoSite)),
        tap((seoData: SeoData) => this.seoService.setSeoData(seoData))
      )
  )(this.cmsContent$);

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsContent: this.cmsContent$,
    isTablet: determineIsTablet(),
  });

  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    @Inject(CMS_CONFIG) private readonly cmsConfig: CmsConfig,
    private readonly cmsFacade: CmsFacade,
    private readonly seoService: SeoService,
    private readonly sessionExpiredModalService: SessionExpiredModalService,
    private readonly themeService: ThemeService,
    private readonly faviconService: FaviconService
  ) {
    super({});

    this.sessionExpiredModalService.detectInactivity();
    this.themeService.applyTheme();
    this.faviconService.applyFavicon();
  }

  private getRootCmsContent(): Observable<CmsRegistrationRoot> {
    const cmsRootPath = this.config.cmsRootPath;
    return this.cmsFacade
      .cmsPageContentByPath<CmsRegistrationRoot>(cmsRootPath)
      .pipe(
        first(),
        map((cmsContent: CmsRegistrationRoot) =>
          formatCmsContent(this.cmsConfig.cmsApi, cmsContent)
        )
      );
  }
}
